import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { COMPOSITION_BUFFER_MODE } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ROUTER_CONFIGURATION, Router } from '@angular/router';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import * as Sentry from '@sentry/angular';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarRouterModule } from '@ngx-loading-bar/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CookieModule, CookieService } from 'ngx-cookie';

import { MessageService } from 'src/app/service/message.service';
import { AppRoutingModule, RouterConfiguration } from './app-routing.module';
import { AppComponent } from './app.component';
import { AdminPageModule } from './page/admin/admin.module';
import { HostPageModule } from './page/host/host.module';
import { ChannelService } from './service/channel.service';
import { ErrorHttpInterceptor } from './service/error-http-interceptor';
import { TokenInterceptor } from './service/token.interceptor';
import { SharedModule } from './shared.module';
import { registerLocaleData } from '@angular/common';
import localeKo from '@angular/common/locales/ko';

registerLocaleData(localeKo);

export function jwtOptionsFactory(cookieService: CookieService) {
  return {
    tokenGetter: () => {
      return cookieService.get('access_token');
    },
  };
}

// ngx-translate HTTP loader factory
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    SharedModule,
    CookieModule.forRoot(),
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [CookieService],
      },
    }),
    LoadingBarRouterModule,
    LoadingBarModule,
    TranslateModule.forRoot({
      defaultLanguage: 'ko',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    AdminPageModule,
    HostPageModule,
  ],
  providers: [
    // Sentry Error Handler
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({ showDialog: false }),
    },
    // Sentry Trace Service for performance monitoring
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    { provide: COMPOSITION_BUFFER_MODE, useValue: false },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHttpInterceptor,
      multi: true,
      deps: [Router, MessageService],
    },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    ChannelService,
    { provide: LOCALE_ID, useValue: 'ko' },
    RouterConfiguration,
    { provide: ROUTER_CONFIGURATION, useExisting: RouterConfiguration },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
